import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import ApplicationRoleModel from '../../models/ApplicationRoleModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import ApplicationFeatureControl from './Components/ApplicationFeatureControl';
import ApplicationRole_tab_AttachmentTypes from './Tabs/ApplicationRole_tab_AttachmentTypes';
import ApplicationRole_tab_ExportTypes from './Tabs/ApplicationRole_tab_ExportTypes';
import ApplicationRole_tab_ImportTypes from './Tabs/ApplicationRole_tab_ImportTypes';
import ApplicationRole_tab_ItemTypes from './Tabs/ApplicationRole_tab_ItemTypes';
import ApplicationRole_tab_PriceTypes from './Tabs/ApplicationRole_tab_PriceTypes';
import ApplicationRole_tab_ProjectTypes from './Tabs/ApplicationRole_tab_ProjectTypes';
import ApplicationRole_tab_Users from './Tabs/ApplicationRole_tab_Users';
import ApplicationRole_tab_WorkflowDefinitions from './Tabs/ApplicationRole_tab_WorkflowDefinitions';

const ApplicationRoleView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<DataResult<ApplicationRoleModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const [formData, setFormData] = useState<ApplicationRoleModel>();

    let idParams: IdParams = useParams();
    let applicationRoleId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        applicationRoleId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (applicationRoleId !== undefined) {
            DataService.getById<ApplicationRoleModel>('api/v1/applicationroles/', applicationRoleId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [applicationRoleId]);

    function submit() {
        if (formData) {
            DataService
                .update<ApplicationRoleModel>(`api/v1/applicationroles/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
            { name: 'generalFeatures.applicationFunctionIds', label: '' },
            { name: 'itemFeatures.applicationFunctionIds', label: '' },
            { name: 'itemSetupFeatures.applicationFunctionIds', label: '' },
            { name: 'workflowFeatures.applicationFunctionIds', label: '' },
            { name: 'workflowSetupFeatures.applicationFunctionIds', label: '' },
            { name: 'projectFeatures.applicationFunctionIds', label: '' },
            { name: 'projectSetupFeatures.applicationFunctionIds', label: '' },
            { name: 'applicationSetupFeatures.applicationFunctionIds', label: '' },
            { name: 'userManagementFeatures.applicationFunctionIds', label: '' },
            { name: 'businessPatnerFeatures.applicationFunctionIds', label: '' },
            { name: 'licenseFeatures.applicationFunctionIds', label: '' },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (applicationRoleId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.UpdateApplicationRole)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    if (!formData)
        return <SpinnerOverlay />

    return (
        
        <div className='main-content'>
            <Form title='Application role' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.EditDesignPhase}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('name')} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                        </Col>
                    </Row>
                    <Row>
                        <Col md='4'>
                            <ApplicationFeatureControl applicationFeature={formData.itemFeatures} getFieldProps={getFieldProps} getControlProps={getControlProps} />
                            <ApplicationFeatureControl applicationFeature={formData.itemSetupFeatures} getFieldProps={getFieldProps} getControlProps={getControlProps} />
                        </Col>
                        <Col md='4'>
                            <ApplicationFeatureControl applicationFeature={formData.workflowFeatures} getFieldProps={getFieldProps} getControlProps={getControlProps} />
                            <ApplicationFeatureControl applicationFeature={formData.workflowSetupFeatures} getFieldProps={getFieldProps} getControlProps={getControlProps} />
                            <ApplicationFeatureControl applicationFeature={formData.projectFeatures} getFieldProps={getFieldProps} getControlProps={getControlProps} />
                            <ApplicationFeatureControl applicationFeature={formData.projectSetupFeatures} getFieldProps={getFieldProps} getControlProps={getControlProps} />
                            <ApplicationFeatureControl applicationFeature={formData.businessPatnerFeatures} getFieldProps={getFieldProps} getControlProps={getControlProps} />
                            <ApplicationFeatureControl applicationFeature={formData.licenseFeatures} getFieldProps={getFieldProps} getControlProps={getControlProps} />
                        </Col>
                        <Col md='4'>
                            <ApplicationFeatureControl applicationFeature={formData.generalFeatures} getFieldProps={getFieldProps} getControlProps={getControlProps} />
                            <ApplicationFeatureControl applicationFeature={formData.applicationSetupFeatures} getFieldProps={getFieldProps} getControlProps={getControlProps} />
                            <ApplicationFeatureControl applicationFeature={formData.userManagementFeatures} getFieldProps={getFieldProps} getControlProps={getControlProps} />
                        </Col>
                    </Row>
                </Container>
            </Form>
            {applicationRoleId  && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='main-tabs'
                                defaultActiveKey={'tab_users'}
                                tabs={[
                                    {
                                        title: 'Users',
                                        eventKey: 'tab_users',
                                        component: <ApplicationRole_tab_Users applicationRoleId={applicationRoleId} />
                                    },
                                    {
                                        title: 'Projecttypes',
                                        eventKey: 'tab_projectTypes',
                                        component: <ApplicationRole_tab_ProjectTypes applicationRoleId={applicationRoleId} />
                                    },
                                    {
                                        title: 'Itemtypes',
                                        eventKey: 'tab_itemTypes',
                                        component: <ApplicationRole_tab_ItemTypes applicationRoleId={applicationRoleId} />
                                    },
                                    {
                                        title: 'Pricetypes',
                                        eventKey: 'tab_priceTypes',
                                        component: <ApplicationRole_tab_PriceTypes applicationRoleId={applicationRoleId} />
                                    },
                                    {
                                        title: 'Attachmenttypes',
                                        eventKey: 'tab_attachmentTypes',
                                        component: <ApplicationRole_tab_AttachmentTypes applicationRoleId={applicationRoleId} />
                                    },
                                    {
                                        title: 'Workflow definitions',
                                        eventKey: 'tab_workflowDefinitions',
                                        component: <ApplicationRole_tab_WorkflowDefinitions applicationRoleId={applicationRoleId} />
                                    },
                                    {
                                        title: 'Export types',
                                        eventKey: 'tab_exportTypes',
                                        component: <ApplicationRole_tab_ExportTypes applicationRoleId={applicationRoleId} />
                                    },
                                    {
                                        title: 'Import types',
                                        eventKey: 'tab_importTypes',
                                        component: <ApplicationRole_tab_ImportTypes applicationRoleId={applicationRoleId} />
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}
        </div>
    );
}
export default ApplicationRoleView;